import React, { useState, useRef, useEffect } from "react";
import PageTitle from "../PageTitle";
import "./styles.css";
import TutorialContent from "./TutorialContent";
import AdvanceButton from "./AdvanceButton";
import Spacer from "../Spacer";
import Instructions from "./TutorialContent/Instructions";
import ScenarioOne from "./TutorialContent/ScenarioOne";
import ScenarioTwo from "./TutorialContent/ScenarioTwo";
import ScenarioThree from "./TutorialContent/ScenarioThree";
import InvestmentOutcomes from "./TutorialContent/InvestmentOutcomes";
import { TutorialsProvider, useTutorialsContext } from "./TutorialsContext";
import InvestmentRoundScreen from "./TutorialContent/InvestmentRoundScreen";
import DiscussionScreen from "./TutorialContent/DiscussionScreen";
import Consent from "./TutorialContent/Consent/Consent";
import { HumanCheckMC, HumanCheckOpen, CombinedPreQuestions } from "./TutorialContent/HumanCheck/HumanCheck";
import ScenarioIntro from "./TutorialContent/ScenarioIntro";
import { usePhaseTracking } from "../../hooks/usePageTracking";
import { getParticipantIdFromSearchParams } from "../../utils/urlParams";

interface TutorialsPageProps {
  onTutorialComplete: () => void; 
}

/** Scenario steps manage Proceed themselves while animations run; step 1 uses form validation. */
const SCENARIO_STEPS = new Set([6, 7, 8]);

function TutorialProceedGate({ step, children }: { step: number; children: React.ReactNode }) {
    const { setDisabled } = useTutorialsContext();

    useEffect(() => {
        if (step === 1) {
            // CombinedPreQuestions manages disabled via validation
            return;
        }
        if (SCENARIO_STEPS.has(step)) {
            // Disable immediately on entry; each Scenario* re-enables when its animation finishes
            setDisabled(true);
            return;
        }
        setDisabled(false);
    }, [step, setDisabled]);

    return <>{children}</>;
}

const TutorialsPage: React.FC<TutorialsPageProps> = ({ onTutorialComplete }) => {
    const [step, setStep] = useState(0);
    const [failedAttentionCheck, setFailedAttentionCheck] = useState(false);
    const [advancePending, setAdvancePending] = useState(false);
    
    // Flag to control tutorial mode (REACT_APP_SKIP_TUTORIAL_MODE in .env overwrites the default)
    const skipTutorialRaw = (process.env.REACT_APP_SKIP_TUTORIAL_MODE || '').trim().toLowerCase();
    const skipTutorialMode = skipTutorialRaw === ''
      ? false
      : ['1', 'true', 'yes', 'on'].includes(skipTutorialRaw);
    
    const totalSteps = skipTutorialMode ? 0 : 9; // Consent + CombinedPreQuestions + Instructions + 2 component screens + ScenarioIntro + 3 scenarios + outcomes summary
    const combinedPreQuestionsRef = useRef<any>(null);

    // Get participant ID from URL (Connect participantId or Prolific ID) for page tracking
    const prolificId = getParticipantIdFromSearchParams(window.location.search);

    // Track tutorial pages with specific step information
    usePhaseTracking('TUTORIAL', prolificId, { 
      tutorialStep: failedAttentionCheck ? undefined : step,
      failedAttentionCheck: failedAttentionCheck 
    });

    const handleAdvance = async () => {
        if (advancePending) return;
        setAdvancePending(true);
        try {
            if (step === 1 && combinedPreQuestionsRef.current?.validateAndCheck) {
                const result = await combinedPreQuestionsRef.current.validateAndCheck();
                if (result === 'ineligible') {
                    setFailedAttentionCheck(true);
                    return;
                }
                if (result === false) {
                    return;
                }
            }
            if (step < totalSteps) {
                setStep(step + 1);
            } else if (step === totalSteps) {
                onTutorialComplete();
            }
        } finally {
            setAdvancePending(false);
        }
    };

    const getTutorialContent = (step: number) => {
        if (skipTutorialMode) {
            return <Instructions />;
        }
        
        switch (step) {
            case 0: return <Consent />;
            case 1: return <CombinedPreQuestions ref={combinedPreQuestionsRef} />;
            case 2: return <Instructions />;
            case 3: return <DiscussionScreen />;
            case 4: return <InvestmentRoundScreen />;
            case 5: return <ScenarioIntro />;
            case 6: return <ScenarioOne />;
            case 7: return <ScenarioTwo />;
            case 8: return <ScenarioThree />;
            case 9: return <InvestmentOutcomes />;
            default: return <Instructions />;
        }
    };

    if (failedAttentionCheck) {
        return (
            <TutorialsProvider>
                <div className="container" style={{ padding: 48, textAlign: 'center' }}>
                    <PageTitle title="Eligibility Failed" />
                    <Spacer />
                    <div style={{ fontSize: 22, color: '#b91c1c', fontWeight: 600 }}>
                    Unfortunately, you are not eligible to participate in this study. So, please just return the task. We appreciate your interest and thank you for your time. 
                    </div>
                </div>
            </TutorialsProvider>
        );
    }

    return (
        <TutorialsProvider>
            <div className="container">
                <PageTitle title="Tutorial" />
                <Spacer />
                <TutorialProceedGate step={step}>
                    <TutorialContent>
                        {getTutorialContent(step)}
                    </TutorialContent>
                </TutorialProceedGate>
                <Spacer />
                <AdvanceButton onAdvance={handleAdvance} pending={advancePending} />
            </div>
        </TutorialsProvider>
    );
};

export default TutorialsPage;